import React, { useState, useEffect } from 'react';
import { FaMapMarkerAlt, FaVolumeUp, FaUser, FaBell, FaCog, FaDollarSign, FaBan, FaChevronRight, FaArrowLeft } from 'react-icons/fa';
import { LocationSettings } from '@/components/Settings/LocationSettings';
import { SoundSettings } from '@/components/Settings/SoundSettings';
import { EmailNotificationSettings } from '@/components/Settings/EmailNotificationSettings';
import { AccountSettings } from '@/components/Settings/AccountSettings';
import { FeesSettings } from '@/components/Settings/FeesSettings';
import { BlockedUsersSettings } from '@/components/Settings/BlockedUsersSettings';
import { ProfileSettings } from '@/components/Settings/ProfileSettings';
import './Settings.scss';

type SettingsTab = 'account' | 'location' | 'sound' | 'profile' | 'notifications' | 'fees' | 'blocked';

interface SettingsTabConfig {
  id: SettingsTab;
  label: string;
  icon: React.ReactNode;
  description: string;
}

const tabs: SettingsTabConfig[] = [
  { id: 'account',       label: 'Account',        icon: <FaCog />,          description: 'Manage your account settings' },
  { id: 'fees',          label: 'Fees',            icon: <FaDollarSign />,   description: 'Set custom pricing for profile interactions' },
  { id: 'location',      label: 'Location',        icon: <FaMapMarkerAlt />, description: 'Manage your home and touring locations' },
  { id: 'sound',         label: 'Sound',           icon: <FaVolumeUp />,     description: 'Configure notification sounds' },
  { id: 'profile',       label: 'Profile',         icon: <FaUser />,         description: 'Update your profile information' },
  { id: 'notifications', label: 'Notifications',   icon: <FaBell />,         description: 'Manage notification preferences' },
  { id: 'blocked',       label: 'Blocked Users',   icon: <FaBan />,          description: 'Manage users you have blocked' },
];

function renderContent(tab: SettingsTab) {
  switch (tab) {
    case 'account':       return <AccountSettings />;
    case 'fees':          return <FeesSettings />;
    case 'location':      return <LocationSettings />;
    case 'sound':         return <SoundSettings />;
    case 'notifications': return <EmailNotificationSettings />;
    case 'blocked':       return <BlockedUsersSettings />;
    case 'profile':   return <ProfileSettings />;
    default: return null;
  }
}

const SettingsPage: React.FC = () => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
  // Desktop always has a tab selected; mobile starts at null (list view)
  const [activeTab, setActiveTab] = useState<SettingsTab | null>(() =>
    window.innerWidth >= 1024 ? 'account' : null
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const handler = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
      if (!e.matches) {
        // Switching to desktop — ensure something is selected
        setActiveTab(t => t ?? 'account');
      } else {
        // Switching to mobile — go back to the list
        setActiveTab(null);
      }
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const activeTabConfig = tabs.find(t => t.id === activeTab);

  // ── Mobile: detail view ───────────────────────────────────────────────────
  if (isMobile && activeTab !== null) {
    return (
      <div className="settings-page settings-page--detail">
        <div className="settings-detail-header">
          <button
            className="settings-back-btn"
            onClick={() => setActiveTab(null)}
            aria-label="Back to settings"
          >
            <FaArrowLeft />
            <span>Settings</span>
          </button>
          <h2 className="settings-detail-title">{activeTabConfig?.label}</h2>
        </div>
        <div className="settings-detail-content">
          {renderContent(activeTab)}
        </div>
      </div>
    );
  }

  // ── Mobile: list view ─────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div className="settings-page settings-page--list">
        <header className="settings-list-header">
          <h1>Settings</h1>
        </header>
        <ul className="settings-list">
          {tabs.map((tab) => (
            <li key={tab.id} className="settings-list-row">
              <button
                className="settings-list-item"
                onClick={() => setActiveTab(tab.id)}
              >
                <span className="settings-list-icon">{tab.icon}</span>
                <span className="settings-list-text">
                  <span className="settings-list-label">{tab.label}</span>
                  <span className="settings-list-desc">{tab.description}</span>
                </span>
                <FaChevronRight className="settings-list-chevron" />
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // ── Desktop: sidebar + content ────────────────────────────────────────────
  return (
    <div className="settings-page">
      <div className="settings-container">
        <header className="settings-header">
          <h1>Settings</h1>
          <p>Manage your Klyx preferences and account settings</p>
        </header>

        <div className="settings-layout">
          <aside className="settings-sidebar">
            <nav className="settings-nav">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  className={`settings-nav-item ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <div className="settings-nav-icon">{tab.icon}</div>
                  <div className="settings-nav-content">
                    <div className="settings-nav-label">{tab.label}</div>
                    <div className="settings-nav-description">{tab.description}</div>
                  </div>
                </button>
              ))}
            </nav>
          </aside>

          <main className="settings-content">
            {activeTab && renderContent(activeTab)}
          </main>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
